import React, { useState, useEffect, useCallback } from 'react';
import { AdminUserDetails as AdminUserDetailsType, AdminActionResponse } from '../types';
import { adminService } from '../services/admin';

interface AdminUserDetailsProps {
  userId: number;
  onNavigateBack: () => void;
}

const AdminUserDetails: React.FC<AdminUserDetailsProps> = ({ userId, onNavigateBack }) => {
  const [userDetails, setUserDetails] = useState<AdminUserDetailsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Action states
  const [actionType, setActionType] = useState<'grant' | 'grantTrial' | 'revoke' | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState<AdminActionResponse | null>(null);

  const fetchUserDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const details = await adminService.getUserDetails(userId);
      setUserDetails(details);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user details');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  const handleUserAction = async (action: 'grant' | 'grantTrial' | 'revoke') => {
    if (!userDetails) return;

    try {
      setActionLoading(true);
      let result: AdminActionResponse;
      
      if (action === 'grant') {
        result = await adminService.grantFreeAccess(userDetails.id, actionReason || 'Admin decision');
      } else if (action === 'grantTrial') {
        result = await adminService.grantTrial(userDetails.id, actionReason || 'Admin decision');
      } else {
        result = await adminService.revokeFreeAccess(userDetails.id, actionReason || 'Admin decision');
      }
      
      setActionResult(result);
      
      // Refresh user details to show updated status
      if (result.success) {
        await fetchUserDetails();
      }
      
      // Clear action after success
      if (result.success) {
        setTimeout(() => {
          setActionType(null);
          setActionReason('');
          setActionResult(null);
        }, 2000);
      }
      
    } catch (err) {
      console.error(`Error ${action === 'revoke' ? 'revoking' : 'granting'} access:`, err);
      setActionResult({ 
        success: false, 
        message: err instanceof Error ? err.message : `Failed to ${action} access` 
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSubscriptionStatusBadge = (status: string) => {
    const statusColors = {
      'trial': 'bg-blue-100 text-blue-800',
      'active': 'bg-green-100 text-green-800',
      'cancelled': 'bg-yellow-100 text-yellow-800',
      'expired': 'bg-red-100 text-red-800',
      'past_due': 'bg-orange-100 text-orange-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };

  const getPlanName = (priceId?: string) => {
    if (!priceId) return 'Standard Plan';
    
    const planNames = {
      'price_standard_monthly': 'Standard Plan (Monthly)',
      'price_standard_yearly': 'Standard Plan (Yearly)',
    };
    
    return planNames[priceId as keyof typeof planNames] || 'Standard Plan';
  };

  const isUserOnFreeAccess = () => {
    if (!userDetails) return false;
    return userDetails.plan === 'professional' && 
           userDetails.subscriptionStatus === 'active' && 
           !userDetails.stripeCustomerId;
  };

  const isUserEligibleForFreeAccess = () => {
    if (!userDetails) return false;
    return userDetails.plan === 'free' || userDetails.subscriptionStatus !== 'active';
  };

  const isUserEligibleForTrial = () => {
    if (!userDetails || userDetails.isAdmin) return false;
    return !(userDetails.subscriptionStatus === 'active' && Boolean(userDetails.stripeSubscriptionId));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/8"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !userDetails) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <button
              onClick={onNavigateBack}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              ← Back to Users
            </button>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Loading User Details</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onNavigateBack}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            ← Back to Users
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Details</h1>
              <p className="mt-2 text-gray-600">Detailed account information for {userDetails.email}</p>
            </div>
            <div className="flex space-x-3">
              {!userDetails.isAdmin && (
                <>
                  {isUserEligibleForTrial() && (
                    <button
                      onClick={() => setActionType('grantTrial')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200"
                    >
                      Grant 3-Month Trial
                    </button>
                  )}
                  {isUserEligibleForFreeAccess() && !isUserOnFreeAccess() && (
                    <button
                      onClick={() => setActionType('grant')}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200"
                    >
                      Grant Free Access
                    </button>
                  )}
                  {isUserOnFreeAccess() && (
                    <button
                      onClick={() => setActionType('revoke')}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition duration-200"
                    >
                      Revoke Free Access
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Profile */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{userDetails.name || 'Not provided'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{userDetails.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Google ID</label>
                  <p className="mt-1 text-sm text-gray-500 font-mono text-xs">{userDetails.googleId}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Status</label>
                  <div className="mt-1 space-y-2">
                    {getSubscriptionStatusBadge(userDetails.subscriptionStatus)}
                    {isUserOnFreeAccess() && (
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          🎁 Admin Free Access
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <div className="mt-1">
                    {userDetails.isAdmin ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Administrator
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">Regular User</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Member Since</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(userDetails.createdAt?.toString())}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                  <p className="mt-1 text-sm text-gray-500">{formatDate(userDetails.updatedAt?.toString())}</p>
                </div>
                {userDetails.trialEndsAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {userDetails.subscriptionStatus === 'expired' ? 'Trial Ended' : 'Trial Ends'}
                    </label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(userDetails.trialEndsAt.toString())}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Plan Information */}
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Plan Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Current Plan</label>
                  <div className="mt-1">
                    {userDetails.plan === 'professional' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Professional Plan
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Free Plan
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription & Billing Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Subscription & Billing Details</h2>
              
              {userDetails.billingInfo ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Subscription Status</label>
                      <div className="mt-1">
                        {getSubscriptionStatusBadge(userDetails.billingInfo.subscriptionStatus)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Plan</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {userDetails.billingInfo.subscription?.priceId 
                          ? getPlanName(userDetails.billingInfo.subscription.priceId) 
                          : 'No active plan'}
                      </p>
                    </div>
                  </div>

                  {userDetails.billingInfo.stripeCustomerId && (
                    <div>
                      <h3 className="text-md font-medium text-gray-900 mb-3">Stripe Information</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Customer ID</label>
                            <p className="mt-1 text-xs text-gray-500 font-mono">{userDetails.billingInfo.stripeCustomerId}</p>
                          </div>
                          {userDetails.billingInfo.stripeSubscriptionId && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Subscription ID</label>
                              <p className="mt-1 text-xs text-gray-500 font-mono">{userDetails.billingInfo.stripeSubscriptionId}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {userDetails.billingInfo.subscription && (
                    <div>
                      <h3 className="text-md font-medium text-gray-900 mb-3">Subscription Details</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Status</label>
                            <p className="mt-1 text-sm text-gray-900 capitalize">
                              {userDetails.billingInfo.subscription.status}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Cancel at Period End</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {userDetails.billingInfo.subscription.cancelAtPeriodEnd ? 'Yes' : 'No'}
                            </p>
                          </div>
                          {userDetails.billingInfo.subscription.currentPeriodEnd && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Current Period End</label>
                              <p className="mt-1 text-sm text-gray-900">
                                {formatTimestamp(userDetails.billingInfo.subscription.currentPeriodEnd)}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No Billing Information</h3>
                  <p className="mt-1 text-sm text-gray-500">This user has no associated billing or subscription data.</p>
                  {isUserOnFreeAccess() && (
                    <p className="mt-2 text-sm text-purple-600">User has admin-granted free access.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Confirmation Modal */}
        {actionType && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 text-center mb-4">
                  {actionType === 'grantTrial'
                    ? 'Grant 3-Month Trial'
                    : actionType === 'grant' ? 'Grant Free Access' : 'Revoke Free Access'}
                </h3>
                <p className="text-sm text-gray-500 text-center mb-4">
                  {actionType === 'grantTrial'
                    ? `Give ${userDetails.email} full Professional access for three months?`
                    : actionType === 'grant'
                    ? `Grant unlimited free access to ${userDetails.email}?` 
                    : `Revoke free access from ${userDetails.email}?`}
                </p>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason (optional)
                  </label>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Enter reason for this action..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    rows={3}
                  />
                </div>

                {actionResult && (
                  <div className={`mb-4 p-3 rounded-md ${actionResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {actionResult.message}
                  </div>
                )}

                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => {
                      setActionType(null);
                      setActionReason('');
                      setActionResult(null);
                    }}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleUserAction(actionType)}
                    disabled={actionLoading}
                    className={`px-4 py-2 rounded-md text-white disabled:opacity-50 ${
                      actionType === 'grantTrial'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : actionType === 'grant'
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {actionLoading ? 'Processing...' : (
                      actionType === 'grantTrial'
                        ? 'Start Trial'
                        : actionType === 'grant' ? 'Grant Access' : 'Revoke Access'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserDetails;